// FILE: ProductList/EnhancedProductList.jsx
// Complete file with improved loading states - Mobile Friendly Version

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiEdit2,
  FiTrash2,
  FiEye,
  FiPackage,
  FiDollarSign,
  FiMapPin,
  FiShield,
  FiAlertTriangle,
  FiCheckCircle,
  FiFilter,
  FiDownload,
  FiUpload,
  FiGrid,
  FiList,
  FiRefreshCw,
  FiTrendingUp,
  FiTrendingDown,
  FiInfo,
  FiPlus,
  FiMoreVertical,
  FiX,
  FiChevronDown,
  FiSearch,
  FiFileText,
} from "react-icons/fi";
import {
  getAllProductsThunk,
  deleteProductByIdThunk,
  updateProductStatusByIdThunk,
  getProductWithPricingThunk,
  getLowStockProductsThunk,
  exportProductsThunk,
  importProductsThunk,
  bulkUpdateProductsThunk,
} from "../../../store/thunks/adminProductThunks";
import { getCategoriesForDropdownThunk } from "../../../store/thunks/categoryThunks";
import Button from "../../../components/common/Button";
import {
  BulkOperationsModal,
  ImportExportSection,
} from "../ProductManagement/AdvancedComponents";
import toast from "react-hot-toast";
import MoreFiltersPanel from "./MoreFiltersPanel";
import { BOTTOM_NAV_HEIGHT } from "components/layout/BottomTabNavigation";
import {
  ProductGridSkeleton,
  ProductListSkeleton,
  CardOperationOverlay,
  InlineSpinner,
} from "./ProductListSkeletons";
import PersonalizedPDFExportModal from "./PersonalizedPDFExportModal";

// Storage keys for persisting filter state
const STORAGE_KEYS = {
  FILTERS: "productList_filters",
  USER_CHANGED_BRANCH: "productList_userChangedBranch",
  LAST_SELECTED_BRANCH: "productList_lastSelectedBranch",
};

const EnhancedProductList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const {
    products = [],
    loading,
    pagination,
    lowStockProducts = [],
    updatingCards = [],
    deletingCards = [], // New: track deleting cards
  } = useSelector((state) => state.adminProduct);

  const { user } = useSelector((state) => state.auth);
  const { branches = [], loading: branchLoading } = useSelector(
    (state) => state.branch,
  );
  const { selectedBranch } = useSelector((state) => state.rate.ui);
  const { dropdownCategories = [], dropdownLoading } = useSelector(
    (state) => state.category,
  );

  // Local state
  const [viewMode, setViewMode] = useState("grid");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showImportExport, setShowImportExport] = useState(false);
  const [showLowStock, setShowLowStock] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showPDFExport, setShowPDFExport] = useState(false);

  // Local deleting state (fallback if not in Redux)
  const [localDeletingCards, setLocalDeletingCards] = useState([]);

  // Combine Redux and local deleting states
  const allDeletingCards = useMemo(
    () => [...(deletingCards || []), ...localDeletingCards],
    [deletingCards, localDeletingCards],
  );

  // Pricing state
  const [showPricing, setShowPricing] = useState(false);
  const [productPricing, setProductPricing] = useState({});
  const [loadingPricing, setLoadingPricing] = useState({});

  // Get all available branch codes
  const availableBranches = useMemo(
    () => branches.map((branch) => branch.code),
    [branches],
  );

  // Initialize filters from localStorage or default values
  const getInitialFilters = () => {
    try {
      const savedFilters = sessionStorage.getItem(STORAGE_KEYS.FILTERS);
      if (savedFilters) {
        return JSON.parse(savedFilters);
      }
    } catch (error) {
      console.error("Error loading saved filters:", error);
    }

    return {
      search: "",
      type: "",
      status: "",
      branchCode: selectedBranch || "",
      inStock: "",
      isHallmarked: "",
      minGrossWeight: "",
      maxGrossWeight: "",
      purity: "",
      subType: "",
      gender: "",
      metalColor: "",
      isFeatured: "",
      isNewArrival: "",
      isBestseller: "",
      minPrice: "",
      maxPrice: "",
      category: "",
      page: 1,
      limit: 20,
    };
  };

  const getInitialUserChangedFlag = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEYS.USER_CHANGED_BRANCH);
      return saved === "true";
    } catch (error) {
      console.error("Error loading user changed flag:", error);
      return false;
    }
  };

  const getLastSelectedBranch = () => {
    try {
      return sessionStorage.getItem(STORAGE_KEYS.LAST_SELECTED_BRANCH) || "";
    } catch (error) {
      console.error("Error loading last selected branch:", error);
      return "";
    }
  };

  const [filters, setFilters] = useState(getInitialFilters);
  const [userHasChangedBranchFilter, setUserHasChangedBranchFilter] = useState(
    getInitialUserChangedFlag,
  );

  // Check if this is the initial load (no products yet)
  const isInitialLoad = loading && products.length === 0;

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEYS.FILTERS, JSON.stringify(filters));
    } catch (error) {
      console.error("Error saving filters:", error);
    }
  }, [filters]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEYS.USER_CHANGED_BRANCH,
        userHasChangedBranchFilter.toString(),
      );
    } catch (error) {
      console.error("Error saving user changed flag:", error);
    }
  }, [userHasChangedBranchFilter]);

  // Fetch categories for dropdown filter (same pattern as CategoryForm)
  useEffect(() => {
    dispatch(getCategoriesForDropdownThunk());
  }, [dispatch]);

  // FIX: Include ALL filter fields in memoizedFilters dependencies
  const memoizedFilters = useMemo(
    () => filters,
    [
      filters.search,
      filters.type,
      filters.status,
      filters.branchCode,
      filters.inStock,
      filters.isHallmarked,
      filters.minGrossWeight,
      filters.maxGrossWeight,
      filters.purity,
      filters.subType,
      filters.gender,
      filters.metalColor,
      filters.isFeatured,
      filters.isNewArrival,
      filters.isBestseller,
      filters.minPrice,
      filters.maxPrice,
      filters.category,
      filters.page,
      filters.limit,
    ],
  );

  const displayBranchFilter = useMemo(() => {
    if (!filters.branchCode) return "All Branches";
    if (Array.isArray(filters.branchCode)) {
      if (filters.branchCode.length === 1) {
        const branch = branches.find((b) => b.code === filters.branchCode[0]);
        return branch
          ? `${branch.code} - ${branch.name || ""}`
          : filters.branchCode[0];
      }
      return `${filters.branchCode.length} Branches`;
    }
    const branch = branches.find((b) => b.code === filters.branchCode);
    return branch
      ? `${branch.code} - ${branch.name || ""}`
      : filters.branchCode;
  }, [filters.branchCode, branches]);

  const loadProducts = useCallback(
    async (showRefreshIndicator = false) => {
      if (showRefreshIndicator) {
        setIsRefreshing(true);
      }
      try {
        await dispatch(getAllProductsThunk(memoizedFilters));
      } finally {
        setIsRefreshing(false);
      }
    },
    [dispatch, memoizedFilters],
  );

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (showLowStock) {
      dispatch(
        getLowStockProductsThunk({
          branchCode: filters.branchCode,
        }),
      );
    }
  }, [showLowStock, filters.branchCode, dispatch]);

  useEffect(() => {
    const lastKnownBranch = getLastSelectedBranch();
    const branchHasChanged = selectedBranch !== lastKnownBranch;

    if (selectedBranch && branchHasChanged) {
      try {
        sessionStorage.setItem(
          STORAGE_KEYS.LAST_SELECTED_BRANCH,
          selectedBranch,
        );
      } catch (error) {
        console.error("Error saving last selected branch:", error);
      }

      if (!userHasChangedBranchFilter) {
        setFilters((prev) => ({
          ...prev,
          branchCode: selectedBranch,
        }));
      }
    }
  }, [selectedBranch, userHasChangedBranchFilter]);

  const loadProductPricing = async (productId) => {
    setLoadingPricing((prev) => ({ ...prev, [productId]: true }));
    try {
      const result = await dispatch(
        getProductWithPricingThunk({
          id: productId,
          options: {
            branchCode: filters.branchCode || user?.branchAccess?.primaryBranch,
            customerType: "RETAIL",
            quantity: 1,
          },
        }),
      ).unwrap();

      setProductPricing((prev) => ({
        ...prev,
        [productId]: result.pricing || result.data?.pricing,
      }));
    } catch (error) {
      console.error("Failed to load pricing for product:", productId);
      toast.error("Failed to load pricing");
    } finally {
      setLoadingPricing((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const handleSelectProduct = useCallback((productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId],
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map((p) => p._id));
    }
  }, [selectedProducts.length, products]);

  const getLocationStock = useCallback(
    (product) => {
      if (!filters.branchCode || Array.isArray(filters.branchCode)) {
        return {
          available: product.stock?.availableQuantity || 0,
          reserved: product.stock?.reservedQuantity || 0,
        };
      }

      const location = product.stock?.locationStock?.find(
        (loc) => loc.branchCode === filters.branchCode,
      );
      return location || { available: 0, reserved: 0 };
    },
    [filters.branchCode],
  );

  const handleDeleteProduct = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      // Add to local deleting state
      setLocalDeletingCards((prev) => [...prev, productId]);

      try {
        await dispatch(deleteProductByIdThunk(productId)).unwrap();
        toast.success("Product deleted successfully");
        // Remove from selected if it was selected
        setSelectedProducts((prev) => prev.filter((id) => id !== productId));
      } catch (error) {
        toast.error(error.message || "Failed to delete product");
      } finally {
        // Remove from local deleting state
        setLocalDeletingCards((prev) => prev.filter((id) => id !== productId));
      }
    }
  };

  const handleStatusChange = async (productId, newStatus) => {
    try {
      await dispatch(
        updateProductStatusByIdThunk({
          id: productId,
          status: newStatus,
        }),
      ).unwrap();
      toast.success("Status updated successfully");
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    }
  };

  const handleViewPricing = async (productId) => {
    try {
      const result = await dispatch(
        getProductWithPricingThunk({
          id: productId,
          options: {
            branchCode: filters.branchCode,
            customerType: "RETAIL",
            quantity: 1,
          },
        }),
      ).unwrap();

      setExpandedProduct(result.data);
    } catch (error) {
      toast.error("Failed to load pricing");
    }
  };

  const handleBulkOperation = async (updates) => {
    try {
      await dispatch(bulkUpdateProductsThunk(updates)).unwrap();
      toast.success("Bulk operation completed");
      setShowBulkModal(false);
      setSelectedProducts([]);
      loadProducts();
    } catch (error) {
      toast.error("Bulk operation failed");
    }
  };

  const handleImport = async (file) => {
    try {
      await dispatch(
        importProductsThunk({
          file,
          defaultBranch: filters.branchCode,
        }),
      ).unwrap();
      toast.success("Products imported successfully");
      loadProducts();
    } catch (error) {
      toast.error("Import failed");
    }
  };

  const handleExport = async (format) => {
    try {
      await dispatch(
        exportProductsThunk({
          format,
          ...filters,
        }),
      ).unwrap();
      toast.success("Export completed");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleFilterChange = (newFilters) => {
    if (
      typeof newFilters === "function"
        ? newFilters(filters).branchCode !== filters.branchCode
        : newFilters.branchCode !== filters.branchCode
    ) {
      setUserHasChangedBranchFilter(true);
    }

    if (typeof newFilters === "function") {
      setFilters(newFilters);
    } else {
      setFilters(newFilters);
    }
  };

  const resetFiltersToDefault = () => {
    setUserHasChangedBranchFilter(false);
    setFilters({
      search: "",
      type: "",
      status: "",
      branchCode: selectedBranch || "",
      inStock: "",
      isHallmarked: "",
      minGrossWeight: "",
      maxGrossWeight: "",
      purity: "",
      subType: "",
      gender: "",
      metalColor: "",
      isFeatured: "",
      isNewArrival: "",
      isBestseller: "",
      minPrice: "",
      maxPrice: "",
      category: "",
      page: 1,
      limit: 20,
    });
    try {
      sessionStorage.removeItem(STORAGE_KEYS.FILTERS);
      sessionStorage.removeItem(STORAGE_KEYS.USER_CHANGED_BRANCH);
    } catch (error) {
      console.error("Error clearing storage:", error);
    }
  };

  const getStockStatus = (product) => {
    const stock = product.stock?.availableQuantity || 0;
    const minLevel = product.stock?.minStockLevel || 1;

    if (stock === 0) return { status: "out-of-stock", color: "red" };
    if (stock <= minLevel) return { status: "low-stock", color: "yellow" };
    return { status: "in-stock", color: "green" };
  };

  const displayProducts = showLowStock ? lowStockProducts : products;

  // FIX: Count ALL active filters including MoreFiltersPanel fields
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.type) count++;
    if (filters.status) count++;
    if (filters.branchCode) count++;
    if (filters.inStock) count++;
    if (filters.isHallmarked) count++;
    if (filters.minGrossWeight) count++;
    if (filters.maxGrossWeight) count++;
    if (filters.purity) count++;
    if (filters.subType) count++;
    if (filters.gender) count++;
    if (filters.metalColor) count++;
    if (filters.isFeatured) count++;
    if (filters.isNewArrival) count++;
    if (filters.isBestseller) count++;
    if (filters.minPrice) count++;
    if (filters.maxPrice) count++;
    if (filters.category) count++;
    return count;
  }, [filters]);

  // Helper to get display label for filter values
  const getFilterDisplayLabel = (field, value) => {
    const labelMap = {
      purity: { label: "Purity", value },
      subType: {
        label: "Sub Type",
        value: value.charAt(0) + value.slice(1).toLowerCase().replace("_", " "),
      },
      gender: {
        label: "Gender",
        value: value.charAt(0) + value.slice(1).toLowerCase(),
      },
      metalColor: {
        label: "Color",
        value: value.charAt(0) + value.slice(1).toLowerCase().replace("_", " "),
      },
      isFeatured: { label: "", value: "Featured" },
      isNewArrival: { label: "", value: "New Arrival" },
      isBestseller: { label: "", value: "Bestseller" },
    };
    const mapped = labelMap[field];
    if (!mapped) return value;
    return mapped.label ? `${mapped.label}: ${mapped.value}` : mapped.value;
  };

  // Resolve category name for filter pill display
  const selectedCategoryName = useMemo(() => {
    if (!filters.category) return "";
    const cat = dropdownCategories.find((c) => c._id === filters.category);
    return cat?.name || filters.category;
  }, [filters.category, dropdownCategories]);

  // Render products content based on loading state
  const renderProductsContent = () => {
    // Initial load - show skeleton
    if (isInitialLoad) {
      return viewMode === "grid" ? (
        <ProductGridSkeleton count={8} />
      ) : (
        <ProductListSkeleton count={8} />
      );
    }

    // No products found
    if (displayProducts.length === 0) {
      return (
        <div className="text-center py-12 sm:py-16">
          <FiPackage className="mx-auto text-4xl sm:text-5xl text-gray-400 mb-4" />
          <p className="text-gray-500 mb-4">No products found</p>
          <button
            onClick={() => navigate("/products/new")}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 transition-colors"
          >
            Add Your First Product
          </button>
        </div>
      );
    }

    // Show products
    return viewMode === "grid" ? (
      <ProductGridView
        products={displayProducts}
        selectedProducts={selectedProducts}
        onSelectProduct={handleSelectProduct}
        onEditProduct={(id) => navigate(`/products/edit/${id}`)}
        onDeleteProduct={handleDeleteProduct}
        onViewPricing={handleViewPricing}
        onStatusChange={handleStatusChange}
        getStockStatus={getStockStatus}
        getLocationStock={getLocationStock}
        expandedProduct={expandedProduct}
        updatingCards={updatingCards}
        deletingCards={allDeletingCards}
        branchCode={filters.branchCode}
        productPricing={productPricing}
        loadingPricing={loadingPricing}
        loadProductPricing={loadProductPricing}
        user={user}
        navigate={navigate}
      />
    ) : (
      <ProductListView
        products={displayProducts}
        selectedProducts={selectedProducts}
        onSelectProduct={handleSelectProduct}
        onSelectAll={handleSelectAll}
        onEditProduct={(id) => navigate(`/products/edit/${id}`)}
        onDeleteProduct={handleDeleteProduct}
        onViewPricing={handleViewPricing}
        onStatusChange={handleStatusChange}
        getStockStatus={getStockStatus}
        getLocationStock={getLocationStock}
        updatingCards={updatingCards}
        deletingCards={allDeletingCards}
        branchCode={filters.branchCode}
        productPricing={productPricing}
        loadingPricing={loadingPricing}
        loadProductPricing={loadProductPricing}
        user={user}
        navigate={navigate}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Responsive Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6">
          {/* Main Header Row */}
          <div className="flex items-center justify-between py-3 sm:py-4">
            {/* Title Section */}
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 truncate">
                Products
              </h1>
              <p className="hidden sm:block text-xs sm:text-sm text-gray-500 mt-0.5">
                Manage your jewelry inventory
              </p>
            </div>

            {/* Action Buttons - Desktop/Tablet */}
            <div className="hidden md:flex items-center gap-2 lg:gap-3">
              {/* Low Stock Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLowStock(!showLowStock)}
                className={`${
                  showLowStock ? "bg-yellow-50" : ""
                } whitespace-nowrap`}
              >
                <FiAlertTriangle className="mr-1.5 lg:mr-2" size={16} />
                <span className="hidden lg:inline">Low Stock</span>
                <span className="lg:hidden">Low</span>
                <span className="ml-1">({lowStockProducts.length})</span>
              </Button>

              {/* Import/Export Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowImportExport(!showImportExport)}
                className="whitespace-nowrap"
              >
                <FiDownload className="mr-1.5 lg:mr-2" size={16} />
                <span className="hidden lg:inline">Import/Export</span>
                <span className="lg:hidden">I/E</span>
              </Button>

              {/* Bulk Add Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/products/new-bulk")}
                className="whitespace-nowrap"
              >
                <FiUpload className="mr-1.5 lg:mr-2" size={16} />
                <span className="hidden xl:inline">Bulk Add</span>
                <span className="xl:hidden">Bulk</span>
              </Button>

              {/* PDF Catalog Export */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPDFExport(true)}
                className="whitespace-nowrap"
              >
                <FiFileText className="mr-1.5 lg:mr-2" size={16} />
                <span className="hidden lg:inline">PDF Catalog</span>
                <span className="lg:hidden">PDF</span>
              </Button>

              {/* Add Product Button */}
              <Button
                size="sm"
                onClick={() => navigate("/products/new")}
                className="whitespace-nowrap"
              >
                <FiPlus className="mr-1.5 lg:mr-2" size={16} />
                <span className="hidden lg:inline">Add Product</span>
                <span className="lg:hidden">Add</span>
              </Button>
            </div>

            {/* Mobile Action Buttons */}
            <div className="flex md:hidden items-center gap-1.5">
              <button
                onClick={() => setShowMobileSearch(!showMobileSearch)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <FiSearch size={20} />
              </button>
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <FiMoreVertical size={20} />
              </button>
            </div>
          </div>

          {/* Mobile Search Bar */}
          {showMobileSearch && (
            <div className="pb-3 md:hidden">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={filters.search}
                  onChange={(e) =>
                    handleFilterChange((prev) => ({
                      ...prev,
                      search: e.target.value,
                      page: 1,
                    }))
                  }
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>

        {/* Mobile Menu Dropdown */}
        {showMobileMenu && (
          <>
            <div
              className="fixed inset-0 bg-black/20 z-40"
              onClick={() => setShowMobileMenu(false)}
            />
            <div className="absolute right-4 top-14 bg-white rounded-lg shadow-lg border border-gray-200 z-50 w-56">
              <div className="py-2">
                <button
                  onClick={() => {
                    setShowLowStock(!showLowStock);
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
                >
                  <FiAlertTriangle
                    className={showLowStock ? "text-warning" : "text-gray-500"}
                  />
                  <span>Low Stock ({lowStockProducts.length})</span>
                </button>
                <button
                  onClick={() => {
                    setShowImportExport(!showImportExport);
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
                >
                  <FiDownload className="text-gray-500" />
                  <span>Import/Export</span>
                </button>
                <button
                  onClick={() => {
                    loadProducts(true);
                    setShowMobileMenu(false);
                  }}
                  disabled={isRefreshing}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 disabled:opacity-50"
                >
                  <FiRefreshCw
                    className={`text-gray-500 ${
                      isRefreshing ? "animate-spin" : ""
                    }`}
                  />
                  <span>{isRefreshing ? "Refreshing..." : "Refresh"}</span>
                </button>
                <button
                  onClick={() => {
                    setShowPDFExport(true);
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
                >
                  <FiFileText className="text-gray-500" />
                  <span>PDF Catalog</span>
                </button>
                <hr className="my-2" />
                <button
                  onClick={() => {
                    navigate("/products/new");
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 text-primary font-medium"
                >
                  <FiPlus />
                  <span>Add Product</span>
                </button>
                <button
                  onClick={() => {
                    navigate("/products/new-bulk");
                    setShowMobileMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 text-primary/80 font-medium"
                >
                  <FiUpload />
                  <span>Bulk Add Products</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-4">
        {/* Import/Export Section */}
        {showImportExport && (
          <div className="mb-4 lg:mb-6">
            <ImportExportSection
              onImport={handleImport}
              onExport={handleExport}
            />
          </div>
        )}

        {/* Filters Bar */}
        <div className="bg-white rounded-lg shadow mb-4">
          {/* Filter Bar - Responsive */}
          <div className="p-3 sm:p-4">
            {/* Search & Filters Row */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {/* Search Input - Hidden on mobile (uses header search) */}
              <div className="hidden sm:block relative flex-shrink-0">
                <FiSearch
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={filters.search}
                  onChange={(e) =>
                    handleFilterChange((prev) => ({
                      ...prev,
                      search: e.target.value,
                      page: 1,
                    }))
                  }
                  className="pl-9 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm w-full sm:w-48 lg:w-64"
                />
              </div>

              {/* Filter Dropdowns */}
              <div className="flex items-center gap-2 flex-1 overflow-x-auto pb-1 sm:pb-0 scrollbar-hide">
                {/* Type Filter */}
                <select
                  value={filters.type}
                  onChange={(e) =>
                    handleFilterChange((prev) => ({
                      ...prev,
                      type: e.target.value,
                      page: 1,
                    }))
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary min-w-[100px] sm:min-w-[120px]"
                >
                  <option value="">All Types</option>
                  <option value="GOLD">Gold</option>
                  <option value="SILVER">Silver</option>
                  <option value="PLATINUM">Platinum</option>
                  <option value="DIAMOND">Diamond</option>
                </select>

                {/* Status Filter - Hidden on smallest screens */}
                <select
                  value={filters.status}
                  onChange={(e) =>
                    handleFilterChange((prev) => ({
                      ...prev,
                      status: e.target.value,
                      page: 1,
                    }))
                  }
                  className="hidden xs:block px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary min-w-[100px] sm:min-w-[120px]"
                >
                  <option value="">All Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="DRAFT">Draft</option>
                  <option value="ARCHIVED">Archived</option>
                </select>

                {/* More Filters Button */}
                <button
                  onClick={() => setShowFilters(true)}
                  className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 relative whitespace-nowrap"
                >
                  <FiFilter size={14} />
                  <span className="hidden sm:inline">More</span>
                  <span className="sm:hidden">Filters</span>
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Right Side Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Refresh Button - Desktop only */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadProducts(true)}
                  disabled={isRefreshing}
                  className="hidden sm:flex"
                >
                  <FiRefreshCw
                    className={isRefreshing ? "animate-spin" : ""}
                    size={16}
                  />
                </Button>

                {/* View Toggle */}
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    className={`p-1.5 sm:p-2 rounded transition-all ${
                      viewMode === "grid" ? "bg-white shadow" : ""
                    }`}
                    onClick={() => setViewMode("grid")}
                  >
                    <FiGrid size={16} />
                  </button>
                  <button
                    className={`p-1.5 sm:p-2 rounded transition-all ${
                      viewMode === "list" ? "bg-white shadow" : ""
                    }`}
                    onClick={() => setViewMode("list")}
                  >
                    <FiList size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* FIX: Active Filters Pills - now includes ALL filter fields */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t">
                {filters.type && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    {filters.type}
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({ ...prev, type: "" }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                {filters.status && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    {filters.status}
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({ ...prev, status: "" }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                {filters.branchCode && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    {displayBranchFilter}
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({
                          ...prev,
                          branchCode: "",
                        }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                {filters.inStock && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    {filters.inStock === "true" ? "In Stock" : "Out of Stock"}
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({ ...prev, inStock: "" }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                {filters.isHallmarked && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    {filters.isHallmarked === "true"
                      ? "Hallmarked"
                      : "Not Hallmarked"}
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({
                          ...prev,
                          isHallmarked: "",
                        }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                {(filters.minGrossWeight || filters.maxGrossWeight) && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    Weight: {filters.minGrossWeight || "0"}g -{" "}
                    {filters.maxGrossWeight || "∞"}g
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({
                          ...prev,
                          minGrossWeight: "",
                          maxGrossWeight: "",
                        }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                {(filters.minPrice || filters.maxPrice) && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    Price: ₹{filters.minPrice || "0"} - ₹
                    {filters.maxPrice || "∞"}
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({
                          ...prev,
                          minPrice: "",
                          maxPrice: "",
                        }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                {filters.category && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    Category: {selectedCategoryName}
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({
                          ...prev,
                          category: "",
                        }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                {filters.purity && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    {getFilterDisplayLabel("purity", filters.purity)}
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({
                          ...prev,
                          purity: "",
                        }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                {filters.subType && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    {getFilterDisplayLabel("subType", filters.subType)}
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({
                          ...prev,
                          subType: "",
                        }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                {filters.gender && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    {getFilterDisplayLabel("gender", filters.gender)}
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({
                          ...prev,
                          gender: "",
                        }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                {filters.metalColor && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    {getFilterDisplayLabel("metalColor", filters.metalColor)}
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({
                          ...prev,
                          metalColor: "",
                        }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                {filters.isFeatured === "true" && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    Featured
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({
                          ...prev,
                          isFeatured: "",
                        }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                {filters.isNewArrival === "true" && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    New Arrival
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({
                          ...prev,
                          isNewArrival: "",
                        }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                {filters.isBestseller === "true" && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    Bestseller
                    <button
                      onClick={() =>
                        handleFilterChange((prev) => ({
                          ...prev,
                          isBestseller: "",
                        }))
                      }
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <FiX size={12} />
                    </button>
                  </span>
                )}
                <button
                  onClick={resetFiltersToDefault}
                  className="text-xs text-gray-500 hover:text-gray-700 underline ml-1"
                >
                  Clear all
                </button>
              </div>
            )}

            {/* Background refresh indicator */}
            {loading && !isInitialLoad && (
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-3 pt-3 border-t">
                <InlineSpinner size={14} />
                <span>Updating...</span>
              </div>
            )}

            {/* Bulk Actions */}
            {selectedProducts.length > 0 && (
              <div className="flex items-center justify-between p-3 mt-3 bg-blue-50 rounded-lg">
                <span className="text-sm text-blue-700">
                  {selectedProducts.length} product
                  {selectedProducts.length > 1 ? "s" : ""} selected
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowBulkModal(true)}
                    className="px-3 py-1.5 text-sm bg-white border border-blue-200 rounded-lg hover:bg-blue-50"
                  >
                    <span className="hidden sm:inline">Bulk Operations</span>
                    <span className="sm:hidden">Bulk</span>
                  </button>
                  <button
                    onClick={() => setShowPDFExport(true)}
                    className="px-3 py-1.5 text-sm bg-white border border-blue-200 rounded-lg hover:bg-blue-50"
                  >
                    <span className="hidden sm:inline">Export PDF</span>
                    <span className="sm:hidden">PDF</span>
                  </button>
                  <button
                    onClick={() => setSelectedProducts([])}
                    className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter Modal - Full Screen on Mobile, Centered on Desktop */}
        {showFilters && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setShowFilters(false)}
            />
            <div className="fixed inset-0 sm:inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-50 bg-white sm:rounded-lg md:shadow-2xl md:w-[90vw] md:max-w-[900px] lg:max-w-[1100px] md:h-[85vh] md:max-h-[85vh] flex flex-col overflow-hidden">
              <MoreFiltersPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClose={() => setShowFilters(false)}
                availableBranches={availableBranches}
                branches={branches}
                categories={dropdownCategories}
                categoriesLoading={dropdownLoading}
              />
            </div>
          </>
        )}

        {/* Products Display */}
        {renderProductsContent()}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 sm:gap-4 py-6">
            <Button
              variant="outline"
              size="sm"
              disabled={filters.page === 1 || loading}
              onClick={() =>
                handleFilterChange((prev) => ({
                  ...prev,
                  page: prev.page - 1,
                }))
              }
              className="px-3 sm:px-4"
            >
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </Button>
            <span className="text-sm text-gray-600 px-2">
              <span className="hidden sm:inline">Page </span>
              {filters.page} / {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={filters.page === pagination.totalPages || loading}
              onClick={() =>
                handleFilterChange((prev) => ({
                  ...prev,
                  page: prev.page + 1,
                }))
              }
              className="px-3 sm:px-4"
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Floating Add Button - Mobile/Tablet */}
      <button
        onClick={() => navigate("/products/new")}
        className="md:hidden fixed bottom-20 right-4 sm:right-6 w-14 h-14 bg-primary text-white rounded-full shadow-lg flex items-center justify-center z-20 active:scale-95 transition-transform"
      >
        <FiPlus size={24} />
      </button>

      {/* Bulk Operations Modal */}
      <BulkOperationsModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        selectedProducts={selectedProducts}
        onExecute={handleBulkOperation}
      />

      {/* Personalized PDF Export Modal */}
      <PersonalizedPDFExportModal
        isOpen={showPDFExport}
        onClose={() => setShowPDFExport(false)}
        products={displayProducts}
        selectedProductIds={selectedProducts}
      />
    </div>
  );
};

// Product Grid View Component - Responsive
const ProductGridView = ({ products, ...props }) => (
  <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4">
    {products.map((product) => (
      <ProductCard key={product._id} product={product} {...props} />
    ))}
  </div>
);

// Product List View Component - Mobile Friendly Cards
const ProductListView = ({ products, onSelectAll, ...props }) => (
  <>
    {/* Desktop Table */}
    <div className="hidden lg:block bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left">
              <input
                type="checkbox"
                onChange={onSelectAll}
                checked={props.selectedProducts.length === products.length}
                className="w-4 h-4"
              />
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Product
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Weight
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Stock
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Price
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {products.map((product) => (
            <ProductRow key={product._id} product={product} {...props} />
          ))}
        </tbody>
      </table>
    </div>

    {/* Mobile List Cards */}
    <div className="lg:hidden space-y-3">
      {products.map((product) => (
        <ProductMobileCard key={product._id} product={product} {...props} />
      ))}
    </div>
  </>
);

// Enhanced Product Card Component - Mobile Optimized with better loading
const ProductCard = ({
  product,
  selectedProducts,
  onSelectProduct,
  onEditProduct,
  onDeleteProduct,
  getStockStatus,
  getLocationStock,
  updatingCards,
  deletingCards = [],
  branchCode,
  user,
  navigate,
}) => {
  const stockStatus = getStockStatus(product);
  const locationStock = getLocationStock(product);
  const isUpdating = updatingCards.includes(product._id);
  const isDeleting = deletingCards.includes(product._id);
  const isOperating = isUpdating || isDeleting;
  const [showPricingInfo, setShowPricingInfo] = useState(false);

  const primaryBranch = user?.branchAccess?.primaryBranch;
  const primaryBranchPricing = product.pricing?.branches?.[primaryBranch];

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-lg transition-all duration-200 overflow-hidden relative">
      {/* Operation Overlay */}
      {isOperating && (
        <CardOperationOverlay type={isDeleting ? "deleting" : "updating"} />
      )}

      <div className="relative">
        {/* Selection Checkbox */}
        <div className="absolute top-2 left-2 z-10">
          <label className="flex items-center justify-center w-6 h-6 bg-white/90 rounded border cursor-pointer">
            <input
              type="checkbox"
              checked={selectedProducts.includes(product._id)}
              onChange={() => onSelectProduct(product._id)}
              disabled={isOperating}
              className="w-4 h-4"
            />
          </label>
        </div>

        {/* Product Image */}
        <div className="h-40 sm:h-48 bg-gray-200 relative">
          {product.media?.[0] ? (
            <img
              src={product.media[0].url}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <FiPackage className="text-3xl lg:text-4xl text-gray-400" />
            </div>
          )}

          {/* Price Badge */}
          {primaryBranchPricing?.available &&
            primaryBranchPricing?.breakdown?.finalAmount && (
              <div className="absolute bottom-2 right-2 bg-white/95 backdrop-blur-sm rounded-lg px-2 py-1">
                <p className="text-sm lg:text-lg font-bold text-primary">
                  ₹
                  {primaryBranchPricing.breakdown.finalAmount.toLocaleString(
                    "en-IN",
                  )}
                </p>
              </div>
            )}
        </div>

        {/* Badges */}
        <div className="absolute top-2 right-2 space-y-1">
          {product.hallmark?.isHallmarked && (
            <span className="inline-flex items-center px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
              <FiShield className="mr-1" size={10} />
              Hallmarked
            </span>
          )}
        </div>
      </div>

      <div className="p-3 lg:p-4">
        {/* Product Info */}
        <div className="mb-2">
          <h3 className="font-medium text-gray-900 text-sm lg:text-base line-clamp-1">
            {product.name}
          </h3>
          <p className="text-xs lg:text-sm text-gray-500">
            ID: {product.productId}
          </p>
        </div>

        {/* Type & Status */}
        <div className="flex justify-between items-center mb-2 lg:mb-3">
          <span className="text-xs lg:text-sm text-gray-600">
            {product.type} • {product.subType}
          </span>
          <span
            className={`px-2 py-0.5 text-xs rounded-full ${
              stockStatus.color === "green"
                ? "bg-green-100 text-green-800"
                : stockStatus.color === "yellow"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-red-100 text-red-800"
            }`}
          >
            {stockStatus.status === "in-stock"
              ? "In Stock"
              : stockStatus.status === "low-stock"
                ? "Low"
                : "Out"}
          </span>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-1 text-xs lg:text-sm mb-2 lg:mb-3 bg-gray-50 rounded-lg p-2">
          <div className="text-center">
            <span className="block text-gray-500 text-[10px] lg:text-xs">
              Gross
            </span>
            <span className="font-medium">{product.grossWeight}g</span>
          </div>
          <div className="text-center">
            <span className="block text-gray-500 text-[10px] lg:text-xs">
              Net
            </span>
            <span className="font-medium">{product.netWeight}g</span>
          </div>
          <div className="text-center">
            <span className="block text-gray-500 text-[10px] lg:text-xs">
              Stock
            </span>
            <span className="font-medium">{locationStock.available}</span>
          </div>
          <div className="text-center">
            <span className="block text-gray-500 text-[10px] lg:text-xs">
              Reserved
            </span>
            <span className="font-medium">{locationStock.reserved}</span>
          </div>
        </div>

        {/* Price Info */}
        {primaryBranchPricing?.available && (
          <button
            onClick={() => setShowPricingInfo(!showPricingInfo)}
            disabled={!primaryBranchPricing?.available || isOperating}
            className="w-full mb-2 lg:mb-3 p-2 bg-gray-50 rounded-lg text-left hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Price Details</span>
              <FiChevronDown
                className={`text-gray-400 transition-transform ${
                  showPricingInfo ? "rotate-180" : ""
                }`}
                size={14}
              />
            </div>
          </button>
        )}

        {/* Expandable Pricing */}
        {showPricingInfo && primaryBranchPricing?.available && (
          <div className="mb-3 p-2 bg-gray-50 rounded-lg space-y-1.5 text-xs lg:text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Metal Value</span>
              <span className="font-medium">
                ₹
                {primaryBranchPricing.breakdown?.metalValue?.toLocaleString(
                  "en-IN",
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Making</span>
              <span className="font-medium">
                ₹
                {primaryBranchPricing.breakdown?.makingCharges?.toLocaleString(
                  "en-IN",
                )}
              </span>
            </div>
            {primaryBranchPricing.breakdown?.wastageCharges > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Wastage</span>
                <span className="font-medium">
                  ₹
                  {primaryBranchPricing.breakdown?.wastageCharges?.toLocaleString(
                    "en-IN",
                  )}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">
                GST ({primaryBranchPricing.breakdown?.gst?.rate}%)
              </span>
              <span className="font-medium">
                ₹
                {primaryBranchPricing.breakdown?.gst?.amount?.toLocaleString(
                  "en-IN",
                )}
              </span>
            </div>
            <div className="flex justify-between border-t pt-1.5">
              <span className="font-medium">Total</span>
              <span className="font-bold text-primary">
                ₹
                {primaryBranchPricing.breakdown?.finalAmount?.toLocaleString(
                  "en-IN",
                )}
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => onEditProduct(product._id)}
            disabled={isOperating}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiEdit2 size={14} />
            <span className="hidden sm:inline">Edit</span>
          </button>
          <button
            onClick={() => navigate(`/products/${product._id}`)}
            disabled={isOperating}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiEye size={14} />
            <span className="hidden sm:inline">View</span>
          </button>
          <button
            onClick={() => onDeleteProduct(product._id)}
            disabled={isOperating}
            className="flex items-center justify-center px-3 py-2 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? <InlineSpinner size={14} /> : <FiTrash2 size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
};

// Mobile Card for List View with better loading
const ProductMobileCard = ({
  product,
  selectedProducts,
  onSelectProduct,
  onEditProduct,
  onDeleteProduct,
  getStockStatus,
  getLocationStock,
  updatingCards,
  deletingCards = [],
  user,
  navigate,
}) => {
  const stockStatus = getStockStatus(product);
  const locationStock = getLocationStock(product);
  const isUpdating = updatingCards.includes(product._id);
  const isDeleting = deletingCards.includes(product._id);
  const isOperating = isUpdating || isDeleting;

  const primaryBranch = user?.branchAccess?.primaryBranch;
  const primaryBranchPricing = product.pricing?.branches?.[primaryBranch];

  return (
    <div className="bg-white rounded-lg shadow p-3 relative">
      {/* Operation Overlay */}
      {isOperating && (
        <CardOperationOverlay type={isDeleting ? "deleting" : "updating"} />
      )}

      <div className="flex gap-3">
        {/* Checkbox & Image */}
        <div className="flex-shrink-0">
          <label className="flex items-center justify-center w-5 h-5 mb-2">
            <input
              type="checkbox"
              checked={selectedProducts.includes(product._id)}
              onChange={() => onSelectProduct(product._id)}
              disabled={isOperating}
              className="w-4 h-4"
            />
          </label>
          <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
            {product.media?.[0] ? (
              <img
                src={product.media[0].url}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <FiPackage className="text-xl text-gray-400" />
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start mb-1">
            <div className="min-w-0">
              <h3 className="font-medium text-gray-900 text-sm truncate">
                {product.name}
              </h3>
              <p className="text-xs text-gray-500">{product.productId}</p>
            </div>
            <span
              className={`ml-2 flex-shrink-0 px-2 py-0.5 text-xs rounded-full ${
                stockStatus.color === "green"
                  ? "bg-green-100 text-green-800"
                  : stockStatus.color === "yellow"
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-red-100 text-red-800"
              }`}
            >
              {stockStatus.status === "in-stock"
                ? "In Stock"
                : stockStatus.status === "low-stock"
                  ? "Low"
                  : "Out"}
            </span>
          </div>

          <div className="flex items-center gap-3 text-xs text-gray-600 mb-2">
            <span>{product.type}</span>
            <span>•</span>
            <span>G: {product.grossWeight}g</span>
            <span>•</span>
            <span>Stock: {locationStock.available}</span>
          </div>

          <div className="flex items-center justify-between">
            {primaryBranchPricing?.available ? (
              <span className="font-bold text-primary">
                ₹
                {primaryBranchPricing.breakdown?.finalAmount?.toLocaleString(
                  "en-IN",
                )}
              </span>
            ) : (
              <span className="text-gray-400">-</span>
            )}

            <div className="flex gap-1">
              <button
                onClick={() => onEditProduct(product._id)}
                disabled={isOperating}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                <FiEdit2 size={16} />
              </button>
              <button
                onClick={() => navigate(`/products/${product._id}`)}
                disabled={isOperating}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                <FiEye size={16} />
              </button>
              <button
                onClick={() => onDeleteProduct(product._id)}
                disabled={isOperating}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
              >
                {isDeleting ? (
                  <InlineSpinner size={16} />
                ) : (
                  <FiTrash2 size={16} />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Product Row Component for Desktop Table with better loading
const ProductRow = ({
  product,
  selectedProducts,
  onSelectProduct,
  onEditProduct,
  onDeleteProduct,
  getStockStatus,
  getLocationStock,
  updatingCards,
  deletingCards = [],
  user,
}) => {
  const stockStatus = getStockStatus(product);
  const locationStock = getLocationStock(product);
  const isUpdating = updatingCards.includes(product._id);
  const isDeleting = deletingCards.includes(product._id);
  const isOperating = isUpdating || isDeleting;

  const primaryBranch = user?.branchAccess?.primaryBranch;
  const primaryBranchPricing = product.pricing?.branches?.[primaryBranch];

  return (
    <tr
      className={`hover:bg-gray-50 ${isOperating ? "opacity-60" : ""} ${
        isDeleting ? "bg-red-50" : ""
      }`}
    >
      <td className="px-6 py-4">
        <input
          type="checkbox"
          checked={selectedProducts.includes(product._id)}
          onChange={() => onSelectProduct(product._id)}
          disabled={isOperating}
          className="w-4 h-4"
        />
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
            {product.media?.[0] ? (
              <img
                src={product.media[0].url}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <FiPackage className="text-gray-400" />
              </div>
            )}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">
              {product.name}
            </div>
            <div className="text-sm text-gray-500">{product.productId}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-gray-900">{product.type}</div>
        <div className="text-sm text-gray-500">{product.subType}</div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-gray-900">G: {product.grossWeight}g</div>
        <div className="text-sm text-gray-500">N: {product.netWeight}g</div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-gray-900">{locationStock.available}</div>
        <div className="text-sm text-gray-500">R: {locationStock.reserved}</div>
      </td>
      <td className="px-6 py-4">
        {primaryBranchPricing?.available ? (
          <div>
            <div className="text-sm font-bold text-primary">
              ₹
              {primaryBranchPricing.breakdown?.finalAmount?.toLocaleString(
                "en-IN",
              )}
            </div>
            <div className="text-xs text-gray-500">{primaryBranch}</div>
          </div>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        )}
      </td>
      <td className="px-6 py-4">
        <span
          className={`px-2 py-1 text-xs rounded-full ${
            stockStatus.color === "green"
              ? "bg-green-100 text-green-800"
              : stockStatus.color === "yellow"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-red-100 text-red-800"
          }`}
        >
          {stockStatus.status.replace("-", " ").toUpperCase()}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex gap-2">
          <button
            onClick={() => onEditProduct(product._id)}
            disabled={isOperating}
            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded disabled:opacity-50"
          >
            {isUpdating ? <InlineSpinner size={16} /> : <FiEdit2 size={16} />}
          </button>
          <button
            onClick={() => onDeleteProduct(product._id)}
            disabled={isOperating}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
          >
            {isDeleting ? <InlineSpinner size={16} /> : <FiTrash2 size={16} />}
          </button>
        </div>
      </td>
    </tr>
  );
};

// Re-export the InlineSpinner for use in other components
export { InlineSpinner };

export default EnhancedProductList;
